import React, { useRef, useState } from 'react';
import { Check, Clock, RotateCcw, Upload, Paperclip, X, AlertTriangle } from 'lucide-react';
import type { SalesOrder, SalesOrderPaymentStatus } from '../../../types/salesOrder';
import { formatCurrency } from '../../../utils/format';
import { useLanguage } from '../../../features/language/useLanguage';
import { Button } from '../../../components/common/Button';
import api from '../../../services/api';

interface PaymentPayload {
  paymentStatus: SalesOrderPaymentStatus;
  paymentType: string;
  paymentProofUrl: string | null;
}

interface SalesOrderPaymentModalProps {
  order: SalesOrder;
  currencySymbol: string;
  isSaving?: boolean;
  isError?: boolean;
  readOnly?: boolean;
  onSubmit: (payment: PaymentPayload) => void;
  onCancel: () => void;
}

const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'pdf'];
const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;

const STATUS_OPTIONS: { value: SalesOrderPaymentStatus; icon: typeof Check; activeClass: string }[] = [
  { value: 'unpaid', icon: Clock, activeClass: 'bg-amber-500 text-white shadow-sm' },
  { value: 'paid', icon: Check, activeClass: 'bg-emerald-600 text-white shadow-sm' },
  { value: 'refunded', icon: RotateCcw, activeClass: 'bg-slate-600 text-white shadow-sm' },
];

const getFilename = (path: string) => path.split('/').pop() || path;

/**
 * Focused payment dialog for a sales order. Settles payment_status / type /
 * proof via the dedicated payment endpoint — it never edits items or stock.
 */
export function SalesOrderPaymentModal({
  order,
  currencySymbol,
  isSaving = false,
  isError = false,
  readOnly = false,
  onSubmit,
  onCancel,
}: SalesOrderPaymentModalProps) {
  const { t } = useLanguage();

  const [paymentStatus, setPaymentStatus] = useState<SalesOrderPaymentStatus>(order.paymentStatus ?? 'unpaid');
  const predefinedTypes = [
    t('salesOrder.payment.types.bankTransfer'),
    t('salesOrder.payment.types.cash'),
  ];
  const initialType = order.paymentType?.trim() || t('salesOrder.payment.types.bankTransfer');
  const [paymentType, setPaymentType] = useState<string>(initialType);
  const [paymentProofUrl, setPaymentProofUrl] = useState<string | null>(order.paymentProofUrl ?? null);

  const isCustomType = !predefinedTypes.includes(paymentType);
  const typeSelectValue = isCustomType ? '__other__' : paymentType;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const getExtension = (name: string) => name.split('.').pop()?.toLowerCase() ?? '';

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ext = getExtension(file.name);
    const mimeAllowed = file.type ? ALLOWED_MIME_TYPES.includes(file.type.toLowerCase()) : true;
    if (!ALLOWED_EXTENSIONS.includes(ext) || !mimeAllowed) {
      setUploadError(t('salesOrder.payment.proof.invalidFormat'));
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setUploadError(t('salesOrder.payment.proof.fileTooLarge'));
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setUploading(true);
    setUploadError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/uploads/documents', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPaymentProofUrl(response.data.path);
    } catch (err: unknown) {
      setUploadError(err instanceof Error ? err.message : t('salesOrder.payment.proof.uploadFailed'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDownload = async (path: string) => {
    try {
      const filename = getFilename(path);
      const response = await api.get(`/uploads/documents/${filename}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setUploadError(t('salesOrder.payment.proof.downloadFailed'));
    }
  };

  const handleSubmit = () => {
    onSubmit({
      paymentStatus,
      paymentType: paymentType.trim(),
      paymentProofUrl: paymentProofUrl,
    });
  };

  return (
    <div className="space-y-6">
      {/* Order context — read-only, anchors the dialog to one invoice. */}
      <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-gray-900">{order.orderNumber}</p>
          <p className="truncate text-xs text-gray-500">{order.customer}</p>
        </div>
        <div className="text-right">
          <p className="text-[11px] font-medium uppercase tracking-wider text-gray-400">
            {t('salesOrder.payment.orderTotal')}
          </p>
          <p className="text-base font-bold text-gray-900 tabular-nums">
            {formatCurrency(order.totalAmount, currencySymbol)}
          </p>
        </div>
      </div>

      {/* Payment status — segmented control, the primary choice. */}
      <div>
        <label className="mb-1.5 block text-sm font-medium text-gray-700">
          {t('salesOrder.payment.statusLabel')}
        </label>
        <div className="grid grid-cols-3 gap-1.5 rounded-lg bg-gray-100 p-1">
          {STATUS_OPTIONS.map(({ value, icon: Icon, activeClass }) => {
            const active = paymentStatus === value;
            return (
              <button
                key={value}
                type="button"
                data-test-id={`so-payment-status-${value}`}
                aria-pressed={active}
                disabled={readOnly}
                onClick={() => setPaymentStatus(value)}
                className={`inline-flex items-center justify-center gap-1.5 rounded-md px-2 py-2 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 ${
                  active ? activeClass : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon className="h-4 w-4" />
                {t(`salesOrder.payment.status.${value}`)}
              </button>
            );
          })}
        </div>
      </div>

      {/* Refund affects reporting — make the consequence explicit. */}
      {paymentStatus === 'refunded' && (
        <div className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-3">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
          <p className="text-xs leading-relaxed text-amber-800">{t('salesOrder.payment.refundNote')}</p>
        </div>
      )}

      {/* Payment method */}
      <div>
        <label className="mb-1.5 block text-sm font-medium text-gray-700">
          {t('salesOrder.payment.typeLabel')}
        </label>
        <div className={isCustomType ? 'flex gap-2' : ''}>
          <select
            data-test-id="so-payment-type"
            value={typeSelectValue}
            disabled={readOnly}
            onChange={(e) => {
              const v = e.target.value;
              if (v === '__other__') {
                setPaymentType(isCustomType ? paymentType : '');
              } else {
                setPaymentType(v);
              }
            }}
            className={`block w-full rounded-md border border-gray-300 p-2 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500 disabled:bg-gray-50 disabled:text-gray-400 ${
              isCustomType ? 'max-w-[200px]' : ''
            }`}
          >
            <option value={t('salesOrder.payment.types.bankTransfer')}>{t('salesOrder.payment.types.bankTransfer')}</option>
            <option value={t('salesOrder.payment.types.cash')}>{t('salesOrder.payment.types.cash')}</option>
            <option value="__other__">{t('salesOrder.payment.types.other')}</option>
          </select>
          {isCustomType && (
            <input
              type="text"
              value={paymentType}
              disabled={readOnly}
              onChange={(e) => setPaymentType(e.target.value)}
              placeholder={t('salesOrder.payment.types.placeholder')}
              className="block w-full rounded-md border border-gray-300 p-2 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500 disabled:bg-gray-50 disabled:text-gray-400"
            />
          )}
        </div>
      </div>

      {/* Proof of payment */}
      <div>
        <label className="mb-1.5 block text-sm font-medium text-gray-700">
          {t('salesOrder.payment.proof.label')}
        </label>
        {paymentProofUrl ? (
          <div className="flex items-center gap-2 rounded-md border border-gray-200 bg-white p-2">
            <Paperclip className="h-4 w-4 flex-shrink-0 text-gray-400" />
            <button
              type="button"
              onClick={() => handleDownload(paymentProofUrl)}
              className="flex-1 truncate text-left text-sm text-primary-600 hover:underline"
              title={getFilename(paymentProofUrl)}
            >
              {getFilename(paymentProofUrl)}
            </button>
            {!readOnly && (
              <button
                type="button"
                onClick={() => setPaymentProofUrl(null)}
                className="flex-shrink-0 text-gray-400 hover:text-red-500"
                title={t('salesOrder.payment.proof.remove')}
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        ) : (
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
              onChange={handleFileChange}
              className="hidden"
              data-test-id="so-payment-proof-input"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || readOnly}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-600 shadow-sm transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-200 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Upload className="h-4 w-4" />
              {uploading ? t('salesOrder.payment.proof.uploading') : t('salesOrder.payment.proof.attach')}
            </button>
            <p className="mt-1 text-xs text-gray-400">{t('salesOrder.payment.proof.accepted')}</p>
          </div>
        )}
        {uploadError && (
          <p className="mt-1 text-sm text-red-600" data-test-id="so-payment-proof-error">
            {uploadError}
          </p>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 border-t border-gray-100 pt-4">
        <button
          type="button"
          data-test-id="so-payment-cancel"
          onClick={onCancel}
          className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          {readOnly ? t('inventory.modal.actions.close') : t('auth.common.cancel')}
        </button>
        {!readOnly && (
          <Button
            type="button"
            data-test-id="so-payment-save"
            onClick={handleSubmit}
            isLoading={isSaving}
            isError={isError}
          >
            {t('salesOrder.payment.save')}
          </Button>
        )}
      </div>
    </div>
  );
}
